// src/hooks/useWorkspaceState.ts
import { useState, useEffect, useRef } from 'react';
import type { Strategy } from '../types/Strategy';
import type { StrategyFamily } from '../types/Strategy';
import type { ErrorNode } from '../types/ErrorNode';

import { deserializeNode } from '../utils/serializeNode';

const WORKSPACE_UI_SESSION_KEY = 'moneymaker-workspace-ui';
const WORKSPACE_RESULT_SESSION_CACHE = new Map<string, Record<string, StrategyResult>>();

function inferStrategyFamily(strategy: Partial<Strategy> | undefined): StrategyFamily {
    if (strategy?.strategyFamily) {
        return strategy.strategyFamily;
    }
    const rootType = strategy?.nodes?.[0]?.type;
    if (rootType === 'MeepMeep') {
        return 'meepmeep';
    }
    return 'base';
}

export interface WorkspaceState {
    strategies: Strategy[];
    activeTab: 'build' | 'results' | 'monitor' | 'alerts' | 'guide';
    activeStrategyId: string | 'all' | null;
    globalStart: string | null;
    globalEnd: string | null;
    editingId: string | null;
    errorNode: ErrorNode | null;
    isEvaluating: boolean;
    strategyFamily: StrategyFamily;
}

/** Transient result data that shouldn't be persisted to the database */
export interface StrategyResult {
    result: any;
    error: string | null;
    nature?: string;
    display_type?: string;
    categorical_labels?: Record<number, string>;
    backendStats?: Record<string, number | null> | null;
    chartData?: any[] | null;
    chartSeriesLabels?: Record<string, string> | null;
    overlayResults?: Record<string, any>; // [id]: result_data
    nodeMetadata?: Record<string, unknown>;
    isLoading?: boolean;
    loadingMode?: 'evaluate' | 'quantarc' | 'meepmeep' | null;
    quantArcJobId?: string | null;
    quantArcJobStatus?: 'queued' | 'running' | 'cancelling' | 'completed' | 'failed' | 'cancelled' | null;
    quantArcJobMessage?: string | null;
    meepMeepJobId?: string | null;
    meepMeepJobStatus?: 'queued' | 'running' | 'cancelling' | 'completed' | 'failed' | 'cancelled' | null;
    meepMeepJobMessage?: string | null;
    meepMeepJobStage?: string | null;
    meepMeepCurrentDayCount?: number | null;
    meepMeepTotalDayCount?: number | null;
    meepMeepProgressPct?: number | null;
    meepMeepRequestTree?: any | null;
    selectedMeepMeepDate?: string | null;
    parityReport?: any;
}

export interface UseWorkspaceStateReturn extends WorkspaceState {
    setStrategies: (s: React.SetStateAction<Strategy[]>) => void;
    setActiveTab: (t: React.SetStateAction<WorkspaceState['activeTab']>) => void;
    setActiveStrategyId: (id: React.SetStateAction<string | 'all' | null>) => void;
    setGlobalStart: (s: React.SetStateAction<string | null>) => void;
    setGlobalEnd: (e: React.SetStateAction<string | null>) => void;
    setEditingId: (id: React.SetStateAction<string | null>) => void;
    setErrorNode: (n: React.SetStateAction<ErrorNode | null>) => void;
    setIsEvaluating: (v: React.SetStateAction<boolean>) => void;
    setWorkspaceState: (patch: Partial<WorkspaceState>) => void;
    setStrategyFamily: (m: WorkspaceState['strategyFamily']) => void;
    setStrategyResult: (id: string, result: Partial<StrategyResult>) => void;
    removeStrategyResult: (id: string) => void;
}

/**
 * Custom hook for managing workspace state with persistence
 * Handles all UI state and syncs with parent component for Universe persistence
 */
export function useWorkspaceState(
    workspaceId: string,
    initialState: Partial<WorkspaceState> | undefined,
    setState: (newState: WorkspaceState) => void
): UseWorkspaceStateReturn {
    const sanitizeStrategyForPersistence = (strategy: Strategy): Strategy => {
        const {
            result,
            error,
            overlayResults,
            nodeMetadata,
            isLoading,
            loadingMode,
            quantArcJobId,
            quantArcJobStatus,
            quantArcJobMessage,
            meepMeepJobId,
            meepMeepJobStatus,
            meepMeepJobMessage,
            meepMeepJobStage,
            meepMeepCurrentDayCount,
            meepMeepTotalDayCount,
            meepMeepProgressPct,
            meepMeepRequestTree,
            selectedMeepMeepDate,
            aiReport,
            backendStats,
            chartData,
            chartSeriesLabels,
            parityReport,
            ...persistent
        } = strategy;
        return {
            ...persistent,
            result: null,
            error: null,
            overlays: (persistent.overlays ?? []).map((overlay) => ({
                ...overlay,
                result: {
                    columns: [],
                    data: [],
                },
            })),
        };
    };

    const safeDeserializeNode = (rawNode: any): Strategy['nodes'][number] | null => {
        try {
            return deserializeNode(rawNode);
        } catch (error) {
            console.error('[workspace] Failed to hydrate node from persisted workspace state:', rawNode, error);
            return null;
        }
    };

    const isHydratedNode = (node: Strategy['nodes'][number] | null): node is Strategy['nodes'][number] =>
        node !== null;

    const getDefaultDateRange = () => {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - 60);

        const toInputFormat = (d: Date) => d.toISOString().split('T')[0];

        return {
            start: toInputFormat(startDate),
            end: toInputFormat(endDate),
        };
    };

    const { start, end } = getDefaultDateRange();
    const sessionKey = `${WORKSPACE_UI_SESSION_KEY}:${workspaceId}`;

    const getBrowserUiState = (): Partial<WorkspaceState> => {
        if (typeof window === 'undefined' || typeof localStorage?.getItem !== 'function') {
            return {};
        }
        try {
            const raw = localStorage.getItem(sessionKey);
            if (!raw) {
                return {};
            }
            const parsed = JSON.parse(raw);
            return {
                activeTab: parsed.activeTab,
                activeStrategyId: parsed.activeStrategyId ?? null,
                strategyFamily: parsed.strategyFamily ?? 'base',
            };
        } catch {
            return {};
        }
    };

    const browserUiState = getBrowserUiState();

    // Persistent state
    const [state, setInternalState] = useState<WorkspaceState>(() => {
        // Hydrate strategies by deserializing their node trees
        const rawStrategies = initialState?.strategies ?? [];
        const hydratedStrategies = rawStrategies.map(s => ({
            ...sanitizeStrategyForPersistence(s),
            strategyFamily: inferStrategyFamily(s),
            nodes: (s.nodes || []).map(safeDeserializeNode).filter(isHydratedNode)
        }));

        return {
            strategies: hydratedStrategies,
            activeTab: browserUiState.activeTab ?? 'build',
            activeStrategyId: browserUiState.activeStrategyId ?? null,
            globalStart: initialState?.globalStart ?? start,
            globalEnd: initialState?.globalEnd ?? end,
            editingId: initialState?.editingId ?? null,
            errorNode: initialState?.errorNode ?? null,
            isEvaluating: initialState?.isEvaluating ?? false,
            strategyFamily: browserUiState.strategyFamily ?? inferStrategyFamily(hydratedStrategies[0]),
        };
    });

    // Volatile / Transient state (results)
    const [strategyResults, setStrategyResults] = useState<Record<string, StrategyResult>>(
        () => WORKSPACE_RESULT_SESSION_CACHE.get(workspaceId) ?? {}
    );

    const hasMountedRef = useRef(false);

    // Sync state to parent for Universe persistence (filtered to persistent fields only)
    useEffect(() => {
        if (!hasMountedRef.current) {
            hasMountedRef.current = true;
            return;
        }

        // Only sync database-backed fields. Browser-only navigation state
        // is restored from localStorage instead of being written to the workspace row.
        setState({
            strategies: state.strategies.map(sanitizeStrategyForPersistence),
            globalStart: state.globalStart,
            globalEnd: state.globalEnd,
        } as any);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.strategies, state.globalStart, state.globalEnd]);

    useEffect(() => {
        if (typeof window === 'undefined' || typeof localStorage?.setItem !== 'function') {
            return;
        }
        localStorage.setItem(
            sessionKey,
            JSON.stringify({
                activeTab: state.activeTab,
                activeStrategyId: state.activeStrategyId,
                strategyFamily: state.strategyFamily,
            }),
        );
    }, [sessionKey, state.activeTab, state.activeStrategyId, state.strategyFamily]);

    useEffect(() => {
        WORKSPACE_RESULT_SESSION_CACHE.set(workspaceId, strategyResults);
    }, [workspaceId, strategyResults]);

    // Validate active strategy selection when switching to build tab
    useEffect(() => {
        if (state.activeTab === 'build') {
            if (!state.strategies.find(s => s.id === state.activeStrategyId)) {
                if (state.strategies.length > 0) {
                    setInternalState(prev => ({ ...prev, activeStrategyId: prev.strategies[0].id }));
                }
            }
        }
    }, [state.activeTab, state.activeStrategyId, state.strategies]);

    const setWorkspaceState = (patch: Partial<WorkspaceState>) => {
        setInternalState(prev => ({ ...prev, ...patch }));
    };

    const setStrategyResult = (id: string, result: Partial<StrategyResult>) => {
        setStrategyResults(prev => ({
            ...prev,
            [id]: { ...prev[id], ...result } as StrategyResult
        }));
    };

    const removeStrategyResult = (id: string) => {
        setStrategyResults(prev => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
    };

    const createSetter = <K extends keyof WorkspaceState>(key: K) =>
        (value: React.SetStateAction<WorkspaceState[K]>) => {
            setInternalState(prev => ({
                ...prev,
                [key]: typeof value === 'function' ? (value as any)(prev[key]) : value
            }));
        };

    // Merge transient results back into strategies for UI consumption
    const mergedStrategies = state.strategies.map(s => {
        const transient = strategyResults[s.id] || {};
        const { overlayResults, ...restTransient } = transient;
        return {
            ...s,
            ...restTransient,
            overlays: s.overlays.map(o => ({
                ...o,
                result: overlayResults?.[o.id] ?? o.result
            }))
        };
    });

    return {
        ...state,
        strategies: mergedStrategies,
        setStrategies: createSetter('strategies'),
        setActiveTab: createSetter('activeTab'),
        setActiveStrategyId: createSetter('activeStrategyId'),
        setGlobalStart: createSetter('globalStart'),
        setGlobalEnd: createSetter('globalEnd'),
        setEditingId: createSetter('editingId'),
        setErrorNode: createSetter('errorNode'),
        setIsEvaluating: createSetter('isEvaluating'),
        setWorkspaceState,
        setStrategyFamily: (m: WorkspaceState['strategyFamily']) => setInternalState(prev => ({ ...prev, strategyFamily: m })),
        setStrategyResult,
        removeStrategyResult,
    };
}
